export const ARCADE_GAME_KINDS = ["beat-lanes", "night-drive", "bass-blaster", "pulse-pads", "vine-runner", "vinyl-orbit"] as const;

export type ArcadeGameKind = (typeof ARCADE_GAME_KINDS)[number];

export type ArcadeGenre = "Rhythm" | "Racing" | "Arcade" | "Runner" | "One button";

// The card shown before a game starts. Keep each line short enough to read at
// a glance on a phone: one goal, a control per line, and a few things to know.
export interface ArcadeHowTo {
  goal: string;
  controls: Array<{ action: string; touch: string; keys: string }>;
  tips: string[];
}

export interface ArcadeGameInfo {
  kind: ArcadeGameKind;
  name: string;
  genre: ArcadeGenre;
  blurb: string;
  touch: string;
  keys: string;
  howTo: ArcadeHowTo;
}

export const ARCADE_GAMES: Record<ArcadeGameKind, ArcadeGameInfo> = {
  "vine-runner": {
    kind: "vine-runner",
    name: "Vine Runner",
    genre: "Runner",
    blurb: "Run the neon vine, stomp the bugs, clear the pits and take down a boss at the end of every level.",
    touch: "Tap to jump, tap again in the air to double jump",
    keys: "Space or Up to jump, again in the air to double jump",
    howTo: {
      goal: "Run the vine and beat the boss at the end of each level.",
      controls: [
        { action: "Jump", touch: "Tap", keys: "Space or Up" },
        { action: "Double jump", touch: "Tap again in the air", keys: "Space or Up again in the air" },
      ],
      tips: [
        "Land on an enemy to stomp it. Hitting one from the side hurts.",
        "Never land on spikes. Jump the gaps, stay low under hanging bars.",
        "Bosses attack on the beat. Dodge, then stomp the glowing core when it dives.",
        "Three lives. Shields and hearts roll in now and then.",
      ],
    },
  },
  "beat-lanes": {
    kind: "beat-lanes",
    name: "Beat Lanes",
    genre: "Rhythm",
    blurb: "Notes stream out of the spinning record down a neon highway. Hit them as they cross the line.",
    touch: "Tap a lane as its note crosses the line",
    keys: "D F J K or the arrow keys",
    howTo: {
      goal: "Hit every note as it crosses the line, in time with the song.",
      controls: [
        { action: "Hit a lane", touch: "Tap the lane", keys: "D F J K or the arrows" },
        { action: "Hold notes", touch: "Keep your finger down", keys: "Keep the key down" },
      ],
      tips: ["Perfect timing scores more than good.", "Misses drain the meter. Empty meter ends the song.", "Long streaks set off Fever for double points."],
    },
  },
  "bass-blaster": {
    kind: "bass-blaster",
    name: "Bass Blaster",
    genre: "Arcade",
    blurb: "Records fly in formations on the beat. Blast the waves, dodge their fire and beat a boss every level.",
    touch: "Drag freely to dodge, you fire on your own",
    keys: "Arrow keys or WASD",
    howTo: {
      goal: "Clear the waves and beat the boss at the end of every level.",
      controls: [
        { action: "Steer", touch: "Drag anywhere", keys: "Arrow keys or WASD" },
        { action: "Fire", touch: "Automatic", keys: "Automatic" },
      ],
      tips: [
        "Only the bright dot on your ship can be hit.",
        "Records flash before they shoot or dive, so watch for the warning.",
        "Clear a whole wave for a power up: T triple, R rapid, S shield, B bass bomb.",
      ],
    },
  },
  "night-drive": {
    kind: "night-drive",
    name: "Night Drive",
    genre: "Racing",
    blurb: "Beat the clock through night traffic, roadworks and crosswalks. Traffic pulls out on the beat.",
    touch: "Drag to steer, tap the top of the screen for nitro",
    keys: "Left and Right to steer, Down to brake, Space for nitro",
    howTo: {
      goal: "Reach each checkpoint before the clock runs out. Every sector has a mission.",
      controls: [
        { action: "Steer", touch: "Drag left and right", keys: "Left and Right, or A and D" },
        { action: "Nitro", touch: "Tap the top of the screen", keys: "Space or Up" },
        { action: "Brake", touch: "Keyboard only", keys: "Down or S" },
      ],
      tips: [
        "Checkpoints add time. Crashes and cones cost it.",
        "Swerve out late from behind a car for a close call.",
        "Oil and puddles make you slide. Let people cross.",
        "Missions pay a life, full nitro or extra time. Three crashes ends the run.",
      ],
    },
  },
  "pulse-pads": {
    kind: "pulse-pads",
    name: "Pulse Pads",
    genre: "Rhythm",
    blurb: "Pads bloom around the record on every beat. Tap each one as its ring closes.",
    touch: "Tap each pad as its ring lands",
    keys: "Click the pads, or Space and Z X on the beat",
    howTo: {
      goal: "Tap each pad as its ring closes, on the beat.",
      controls: [
        { action: "Hit a pad", touch: "Tap it", keys: "Click it, or Space, Z or X on the beat" },
      ],
      tips: ["Hit them in number order.", "Misses drain the meter. Empty meter ends the song.", "Later levels bring chords and faster streams."],
    },
  },
  "vinyl-orbit": {
    kind: "vinyl-orbit",
    name: "Vinyl Orbit",
    genre: "One button",
    blurb: "Circle the record and hop rings to weave past the spikes. Every level ends with the spike wheel.",
    touch: "Tap to switch rings",
    keys: "Space to switch rings",
    howTo: {
      goal: "Circle the record, grab the notes and weave past the spikes.",
      controls: [{ action: "Switch rings", touch: "Tap", keys: "Space" }],
      tips: [
        "Spikes fade in first. They only hurt once they turn solid.",
        "Each level adds a new trick and ends with the spike wheel.",
        "When REVERSE flashes, the spin flips and the board clears.",
      ],
    },
  },
};

// Title and description for one game of one song (/games/:song/:game).
// netlify/functions/seoEnhance.js writes the same words for crawlers and
// src/tests/seo-catalog.test.ts keeps the two in step.
export const gameTitle = (songTitle: string, kind: ArcadeGameKind) => `${songTitle} ${ARCADE_GAMES[kind].name}: Free ${ARCADE_GAMES[kind].genre} Game`;

export const gameDescription = (songTitle: string, artistName: string, kind: ArcadeGameKind) =>
  `Play ${songTitle} by ${artistName} as ${ARCADE_GAMES[kind].name}, a free ${ARCADE_GAMES[kind].genre.toLowerCase()} game in your browser. ${ARCADE_GAMES[kind].howTo.goal}`;

export const isArcadeGameKind = (value: unknown): value is ArcadeGameKind =>
  typeof value === "string" && (ARCADE_GAME_KINDS as readonly string[]).includes(value);

export const ARCADE_DIFFICULTIES = ["chill", "normal", "hype"] as const;
export type ArcadeDifficulty = (typeof ARCADE_DIFFICULTIES)[number];

export const DIFFICULTY_LABELS: Record<ArcadeDifficulty, string> = { chill: "Chill", normal: "Normal", hype: "Hype" };

export const DIFFICULTY_SCALE: Record<ArcadeDifficulty, number> = { chill: 0.8, normal: 1, hype: 1.25 };

export const isArcadeDifficulty = (value: unknown): value is ArcadeDifficulty =>
  typeof value === "string" && (ARCADE_DIFFICULTIES as readonly string[]).includes(value);
